import React, { type PropsWithChildren, createContext, useContext, useState, useEffect, useRef } from 'react';
import type { InfoResponse, AccessInfo, LlmRawOption, PersonaOption, TermsResponse, DocumentInfoType, UserPreferences, WorkflowOptions } from '../../models/types';
import type { IAppInfoService } from '../../services/IAppInfoService';
import { useAuth } from '../auth/AuthContext';
import EventHandler, { NotImplementedEventHandler } from '../../services/EventHandler';
import { PreferencesEvent } from '../../models/BackendEvents';
import { mockGetInfoResponse } from '../../data/mockData';

type Props = {
    service: IAppInfoService;
};

enum AuthenticationErrorDetail {
    AccessBlocked = 'User is blocked',
    GuestBlocked = 'Guest is blocked'
}

export enum AuthenticationState {
    Authenticated = 'Authenticated',
    UnableToAuthenticate = 'UnableToAuthenticate',
    MissingRole = 'MissingRole',
    AccessBlocked = 'AccessBlocked',
    UnknownError = 'UnknownError',
}
interface IAppInfoContext {
    getTerms: () => Promise<TermsResponse>;
    getPersonaVersionReleaseNotes: (persona: string, version: string) => Promise<string>;
    getPersonaVersionWorkflowOption: (persona: string, version?: string, build?: number) => Promise<WorkflowOptions>;
    authenticationState?: AuthenticationState;
    userId?: string;
    isGuest?: boolean;
    llms?: LlmRawOption[];
    personas?: PersonaOption[];
    access?: AccessInfo;
    documents?: DocumentInfoType;
    preferences?: UserPreferences;

    events: {
        preferences: EventHandler<PreferencesEvent>;
    };
    download: (url: string, contentType?: string, downloadFilename?: string) => Promise<boolean>;
    downloadBlob: (url: string, contentType?: string) => Promise<{ blob: Blob, filename: string; }>;
}

const defaultAppInfoContext: IAppInfoContext = {
    authenticationState: undefined,
    userId: undefined,
    llms: [],
    personas: [],
    access: {
        personas: {}
    },
    preferences: {
        personas_pinned: [], personas_disable_auto_pin: [], prompts_pinned: []
    },
    getTerms: function (): Promise<TermsResponse> {
        throw new Error('Function not implemented.');
    },
    getPersonaVersionReleaseNotes: function (): Promise<string> {
        throw new Error('Function not implemented.');
    },
    getPersonaVersionWorkflowOption: function (): Promise<WorkflowOptions> {
        throw new Error('Function not implemented.');
    },
    events: {
        preferences: new NotImplementedEventHandler()
    },
    download: async (url: string, contentType?: string, downloadFilename?: string): Promise<boolean> => {
        throw new Error('Function not implemented.');
    },
    downloadBlob: async (url: string, contentType?: string): Promise<{ blob: Blob, filename: string; }> => {
        throw new Error('Function not implemented.');
    }
};

const AppInfoContext = createContext<IAppInfoContext>(defaultAppInfoContext);

const AppInfoProvider: React.FC<PropsWithChildren<Props>> = ({ service, children }) => {
    const { isLoggingIn, isStartingUp, isLoggingOut } = useAuth();
    const [authenticationState, setAuthenticationState] = useState<AuthenticationState>();

    const [{ llm, personas, access, documents, preferences, user_id: userId, is_guest: isGuest }, setInfoResponse] = useState<Partial<InfoResponse>>({});

    const didInitialize = useRef(false);
    useEffect(() => {
        if (didInitialize.current) return;
        if (isLoggingIn || isStartingUp || isLoggingOut) return setAuthenticationState(undefined);

        (async () => {
            didInitialize.current = true;
            try {
                const result = mockGetInfoResponse;
                setAuthenticationState(AuthenticationState.Authenticated);
                setInfoResponse(result);
            } catch (error: any) {
                switch (error.status) {
                    case 401: {
                        // Unable to authenticate user
                        return setAuthenticationState(AuthenticationState.UnableToAuthenticate);
                    }
                    case 403: {
                        const errorDetail = error.data.detail;
                        // 403   - Access denied (in blocked group)
                        switch (errorDetail) {
                            case AuthenticationErrorDetail.AccessBlocked:
                            case AuthenticationErrorDetail.GuestBlocked:
                                return setAuthenticationState(AuthenticationState.AccessBlocked);
                            default:
                                return setAuthenticationState(AuthenticationState.MissingRole);
                        }
                    }
                    default:
                        return setAuthenticationState(AuthenticationState.UnknownError);
                }
            }
        })();
    }, [isLoggingIn, isLoggingOut, isStartingUp, service]);
    return <AppInfoContext.Provider value={{
        authenticationState,
        userId,
        isGuest,
        llms: llm,
        preferences,
        personas,
        access,
        documents,
        getTerms: service.getTerms,
        getPersonaVersionReleaseNotes: service.getPersonaVersionReleaseNotes,
        getPersonaVersionWorkflowOption: service.getPersonaVersionWorkflowOption,
        events: service.events,
        download: service.download,
        downloadBlob: service.downloadBlob
    }}>
        {children}
    </AppInfoContext.Provider>;
};

export const useAppInfo = () => useContext(AppInfoContext);

export default AppInfoProvider;